/**
 * Get Entity Actions
 * Main function to retrieve actions for an entity
 * Provides a simple, type-safe interface to the registry
 */

import { actionRegistry } from "./registry/ActionRegistry";
import type { EntityKey, EntityRowType } from "./types/entity-keys";
import type { DetailActionStrict } from "./types/detail-action";
import type { ActionContextType } from "./types/action-names";

/**
 * Get all actions for an entity
 * Returns actions typed to the entity's row type
 *
 * @example
 * const contactActions = getEntityActions('contacts');
 * // Type: DetailActionStrict<Contact>[]
 */
export function getEntityActions<K extends EntityKey>(
  entity: K
): DetailActionStrict<EntityRowType<K>>[] {
  return actionRegistry.getActionsForEntity(entity);
}

/**
 * Get actions for an entity filtered by context
 * Returns only actions that apply to the given context
 *
 * @example
 * const detailPageActions = getEntityActionsForContext('contacts', 'detail-page');
 * // Type: DetailActionStrict<Contact>[]
 */
export function getEntityActionsForContext<K extends EntityKey>(
  entity: K,
  context: ActionContextType
): DetailActionStrict<EntityRowType<K>>[] {
  return actionRegistry.getActionsForEntityAndContext(entity, context);
}

/**
 * Check if an entity has registered actions
 */
export function hasEntityActions(entity: EntityKey): boolean {
  return actionRegistry.hasActionsForEntity(entity);
}

export type { EntityKey, EntityRowType } from "./types/entity-keys";
export type { DetailActionStrict } from "./types/detail-action";
export type { ActionContextType, ActionHandlerName, ActionId } from "./types/action-names";
